/**
 * Usage tab — client-side cost/token charts over a date window (from `useRuns`,
 * sharing RunsTab's default 7-day window and `isoDaysAgo`). No charting
 * dependency: the per-day cost bars are plain inline-styled <span>s stacked by
 * PRODUCER (trading's UsageTab technique), sized as integer percent-of-peak and
 * coloured by a stable producer legend.
 *
 * Grouped by producer — the identity a run is filed under — and never by a
 * claimed platform: a run's records may each claim a different one (a run
 * carries a SET of claims), so a per-claim split would count one run's cost
 * under several series, or have to pick one claim arbitrarily.
 *
 * READ-ONLY: the console never mutates. All state is local (`useState`) — the
 * from/to inputs default to the same window as RunsTab so the two tabs agree.
 * The bucketing/aggregation helpers are exported pure so they unit-test without
 * a DOM. Loading/error/empty states mirror RunsTab's house style.
 */

import { useState } from 'react';
import { useRuns, type Run } from '../lib/queries';
import { isoDaysAgo, UnpricedBadge } from './RunsTab';

// Segment palette, cycled by producer legend index (see `producerLegend`).
const SEGMENT_COLORS = ['var(--accent)', 'var(--ok)', 'var(--banner-text)', 'var(--danger)'];

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests — no DOM needed).
// ---------------------------------------------------------------------------

/**
 * The series a run with NO producer is counted under — a rollup written before
 * the producer re-key comes back without one. Named rather than dropped, so the
 * day total still reconciles with the cost tile; it contains a space, which no
 * producer may carry, so it can never collide with a real one.
 */
export const NO_PRODUCER = '(no producer)';

function producerOf(r: Run): string {
  return r.producer ?? NO_PRODUCER;
}

export interface DayCost {
  date: string;
  total: number;
  byProducer: Array<{ producer: string; cost: number }>;
}

/** Bucket run cost by `run_date`, then by producer within each day. Days are
 *  sorted ascending by date; each day's producers descend by cost. */
export function costByDay(runs: Run[]): DayCost[] {
  const byDate = new Map<string, Map<string, number>>();
  for (const r of runs) {
    let producers = byDate.get(r.run_date);
    if (!producers) {
      producers = new Map();
      byDate.set(r.run_date, producers);
    }
    const p = producerOf(r);
    producers.set(p, (producers.get(p) ?? 0) + r.cost_usd);
  }
  return [...byDate.entries()]
    .map(([date, producers]) => {
      const byProducer = [...producers.entries()]
        .map(([producer, cost]) => ({ producer, cost }))
        .sort((a, b) => b.cost - a.cost);
      const total = byProducer.reduce((s, p) => s + p.cost, 0);
      return { date, total, byProducer };
    })
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

export interface ModelTokens {
  model: string;
  input: number;
  output: number;
}

/** Aggregate input/output tokens per model. Multi-model runs attribute to the
 *  sorted-and-joined model key (`models.sort().join(' + ')`) so the same combo
 *  always buckets together. Sorted descending by total (input + output). */
export function tokensByModel(runs: Run[]): ModelTokens[] {
  const byModel = new Map<string, { input: number; output: number }>();
  for (const r of runs) {
    const key = r.models.slice().sort().join(' + ');
    let agg = byModel.get(key);
    if (!agg) {
      agg = { input: 0, output: 0 };
      byModel.set(key, agg);
    }
    agg.input += r.input_tokens;
    agg.output += r.output_tokens;
  }
  return [...byModel.entries()]
    .map(([model, t]) => ({ model, input: t.input, output: t.output }))
    .sort((a, b) => b.input + b.output - (a.input + a.output));
}

export interface WindowTotals {
  runs: number;
  errors: number;
  cost: number;
  tokens: number;
}

/** Roll the whole window into headline counts: run count, summed error_count
 *  (missing → 0), summed cost, and summed input+output tokens. */
export function windowTotals(runs: Run[]): WindowTotals {
  let errors = 0;
  let cost = 0;
  let tokens = 0;
  for (const r of runs) {
    errors += r.error_count ?? 0;
    cost += r.cost_usd;
    tokens += r.input_tokens + r.output_tokens;
  }
  return { runs: runs.length, errors, cost, tokens };
}

/**
 * The window's unpriced turns and the distinct models behind them.
 *
 * Spec §7 puts the indicator on any run OR AGGREGATE, and the aggregate is where
 * it matters most: `windowTotals().cost` and `costByDay()` both SUM `cost_usd`
 * across runs, so an unpriceable model silently under-reports the headline money
 * number — the most prominent figure in the console — while the run one click
 * away carries the badge. Shaped to feed `UnpricedBadge` directly so there is
 * exactly one badge in the codebase.
 */
export function unpricedTotals(runs: Run[]): Pick<Run, 'unpriced_turns' | 'unpriced_models'> {
  let turns = 0;
  const models = new Set<string>();
  for (const r of runs) {
    turns += r.unpriced_turns ?? 0;
    for (const m of r.unpriced_models ?? []) models.add(m);
  }
  return { unpriced_turns: turns, unpriced_models: [...models].sort() };
}

/** Stable, sorted list of the distinct producers in the window — the legend
 *  whose index picks each producer's segment colour. */
function producerLegend(runs: Run[]): string[] {
  return [...new Set(runs.map(producerOf))].sort();
}

/** Fixed 4-dp USD cost, e.g. `$0.1234`. */
function fmtCost(n: number): string {
  return `$${n.toFixed(4)}`;
}

// ---------------------------------------------------------------------------
// The tab.
// ---------------------------------------------------------------------------

export default function UsageTab() {
  // Default window from the SERVER clock via `isoDaysAgo`'s default `end`
  // (#291) — pass no `end` here, or the device clock comes back.
  const [from, setFrom] = useState(() => isoDaysAgo(7));
  const [to, setTo] = useState(() => isoDaysAgo(0));

  const { data, isLoading, isError, error, refetch } = useRuns(from, to);

  const controls = (
    <div className="card runs-controls">
      <label className="runs-control">
        <span className="runs-control-label">From</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
      </label>
      <label className="runs-control">
        <span className="runs-control-label">To</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </label>
    </div>
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = <p className="note">Loading usage…</p>;
  } else if (isError) {
    body = (
      <p className="error" role="alert">
        Failed to load usage: {error instanceof Error ? error.message : 'unknown error'}{' '}
        <button type="button" className="btn" onClick={() => refetch()}>
          Retry
        </button>
      </p>
    );
  } else {
    const runs = data?.runs ?? [];
    if (runs.length === 0) {
      body = <p className="usage-empty note">No runs in this window.</p>;
    } else {
      const totals = windowTotals(runs);
      const unpriced = unpricedTotals(runs);
      const days = costByDay(runs);
      const models = tokensByModel(runs);
      const legend = producerLegend(runs);
      const colorFor = (p: string) =>
        SEGMENT_COLORS[legend.indexOf(p) % SEGMENT_COLORS.length];
      const peak = days.reduce((m, d) => Math.max(m, d.total), 0) || 1;

      body = (
        <>
          <div className="usage-summary">
            <div className="card usage-stat">
              <span className="usage-stat-label">Runs</span>
              <span className="usage-stat-value">{totals.runs.toLocaleString()}</span>
            </div>
            <div className="card usage-stat">
              <span className="usage-stat-label">Errors</span>
              <span className="usage-stat-value">{totals.errors.toLocaleString()}</span>
            </div>
            <div className="card usage-stat">
              <span className="usage-stat-label">Cost</span>
              {/* The badge sits OUTSIDE `.usage-stat-value` on purpose: the tile's
                  value is the number and nothing else. */}
              <span className="usage-stat-value mono">{fmtCost(totals.cost)}</span>
              <UnpricedBadge run={unpriced} />
            </div>
            <div className="card usage-stat">
              <span className="usage-stat-label">Tokens</span>
              <span className="usage-stat-value">{totals.tokens.toLocaleString()}</span>
            </div>
          </div>

          <div className="usage-legend">
            {legend.map((p) => (
              <span key={p} className="usage-legend-item">
                <span
                  className="usage-legend-swatch"
                  style={{ background: colorFor(p) }}
                  aria-hidden="true"
                />
                {p}
              </span>
            ))}
          </div>

          <h3 className="section-title">Cost per day</h3>
          <div className="card">
            {days.map((d) => (
              <div key={d.date} className="usage-day-row">
                <span className="usage-day-date mono">{d.date}</span>
                <span className="usage-day-bar" aria-hidden="true">
                  {d.byProducer.map((p) => (
                    <span
                      key={p.producer}
                      className="usage-bar-seg"
                      style={{
                        width: `${Math.round((p.cost / peak) * 100)}%`,
                        background: colorFor(p.producer),
                      }}
                      title={`${p.producer} ${fmtCost(p.cost)}`}
                    />
                  ))}
                </span>
                <span className="usage-day-total detail-num mono">{fmtCost(d.total)}</span>
              </div>
            ))}
          </div>

          <h3 className="section-title">Tokens by model</h3>
          <table className="detail-table">
            <thead>
              <tr>
                <th>model</th>
                <th className="detail-num">input</th>
                <th className="detail-num">output</th>
                <th className="detail-num">total</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.model}>
                  <td>{m.model || '—'}</td>
                  <td className="detail-num">{m.input.toLocaleString()}</td>
                  <td className="detail-num">{m.output.toLocaleString()}</td>
                  <td className="detail-num">{(m.input + m.output).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    }
  }

  return (
    <section className="usage-tab" aria-label="Usage">
      {controls}
      {body}
    </section>
  );
}
